import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Wallet, Gift, Trash2, CheckCircle, XCircle, Users, BarChart3 } from 'lucide-react';
import './TeamManagement.css';

function TeamManagement({ currentUser }) {
  const [agents, setAgents] = useState([]);
  const [commissionStats, setCommissionStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(currentUser.role === 'sous_admin' ? 'overview' : 'equipe');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusAgent, setBonusAgent] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentStats, setAgentStats] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    password: '',
    commission_attribuee: '0'
  });
  const [bonusData, setBonusData] = useState({
    montant: '',
    motif: ''
  });
  const [error, setError] = useState('');

  // Sous-admin specific state
  const [chefs, setChefs] = useState([]);
  const [showCreateChefModal, setShowCreateChefModal] = useState(false);
  const [chefFormData, setChefFormData] = useState({
    nom: '',
    email: '',
    password: '',
    commission_base: '0'
  });
  const [selectedChef, setSelectedChef] = useState(null);
  const [chefAgents, setChefAgents] = useState([]);

  const isSousAdmin = currentUser.role === 'sous_admin';
  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const promises = [
        axios.get(`${API_URL}/api/chefs-equipe/${currentUser.id}/agents`, { headers }),
        axios.get(`${API_URL}/api/chefs-equipe/${currentUser.id}/commission-stats`, { headers }),
        axios.get(`${API_URL}/api/chefs-equipe/${currentUser.id}/clients`, { headers }),
        axios.get(`${API_URL}/api/chefs-equipe/${currentUser.id}/commissions`, { headers }),
        axios.get(`${API_URL}/api/chefs-equipe/${currentUser.id}/bonuses`, { headers })
      ];

      // Sous-admin: also fetch chefs list
      if (isSousAdmin) {
        promises.push(axios.get(`${API_URL}/api/sous-admin/${currentUser.id}/chefs`, { headers }));
      }

      const results = await Promise.all(promises);

      setAgents(results[0].data);
      setCommissionStats(results[1].data);
      setClients(results[2].data);
      setCommissions(results[3].data);
      setBonuses(results[4].data);

      if (isSousAdmin && results[5]) {
        setChefs(results[5].data);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentStats = async (agentId, chefId) => {
    try {
      const token = localStorage.getItem('token');
      const statsChefId = chefId || currentUser.id;
      const response = await axios.get(
        `${API_URL}/api/chefs-equipe/${statsChefId}/agents/${agentId}/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAgentStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats agent:', error);
    }
  };

  const fetchChefAgents = async (chefId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/chefs-equipe/${chefId}/agents`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChefAgents(response.data);
    } catch (error) {
      console.error('Erreur chargement agents du chef:', error);
    }
  };

  const handleSelectAgent = (agent, chefId) => {
    setSelectedAgent(agent);
    fetchAgentStats(agent.id, chefId);
  };

  const handleSelectChef = (chef) => {
    setSelectedChef(chef);
    setSelectedAgent(null);
    setAgentStats(null);
    fetchChefAgents(chef.id);
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/chefs-equipe/agents`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddModal(false);
      setFormData({ nom: '', email: '', password: '', commission_attribuee: '0' });
      fetchData();
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const handleCreateChef = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/sous-admin/${currentUser.id}/create-chef`, chefFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowCreateChefModal(false);
      setChefFormData({ nom: '', email: '', password: '', commission_base: '0' });
      fetchData();
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur lors de la création du chef');
    }
  };

  const handleUpdateCommission = async (agentId, newCommission) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/chefs-equipe/agents/${agentId}/commission`, {
        commission_attribuee: newCommission
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleRemoveAgent = async (agentId) => {
    if (!window.confirm('Retirer cet agent de votre équipe ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/chefs-equipe/${currentUser.id}/remove-agent/${agentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAgent(null);
      setAgentStats(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors du retrait');
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Supprimer définitivement cet agent ? Cette action est irréversible.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/chefs-equipe/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAgent(null);
      setAgentStats(null);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const openBonusModal = (agent) => {
    setBonusAgent(agent);
    setBonusData({ montant: '', motif: '' });
    setError('');
    setShowBonusModal(true);
  };

  const handleGiveBonus = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/chefs-equipe/${currentUser.id}/bonus`, {
        agent_id: bonusAgent.id,
        montant: bonusData.montant,
        motif: bonusData.motif
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowBonusModal(false);
      setBonusAgent(null);
      setBonusData({ montant: '', motif: '' });
      fetchData();
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur lors de l\'attribution du bonus');
    }
  };

  const handleDeleteBonus = async (bonusId, montant, agentNom) => {
    if (!window.confirm(`Supprimer le bonus de ${montant}€ accordé à ${agentNom} ?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/chefs-equipe/${currentUser.id}/bonuses/${bonusId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de la suppression du bonus');
    }
  };

  const getCommissionDisplay = (agent) => {
    const commissionChef = parseFloat(agent.commission_attribuee || 0);
    return { value: commissionChef, source: 'chef', label: 'Commission attribuée' };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="team-loading">Chargement...</div>;
  }

  return (
    <div className="team-management">
      <div className="team-header">
        <h2>Mon Équipe</h2>
        <div className="team-actions">
          {isSousAdmin && (
            <button className="btn-add" style={{ marginRight: '10px', background: '#f57c00' }} onClick={() => setShowCreateChefModal(true)}>
              + Créer un chef d'équipe
            </button>
          )}
          <button className="btn-add" onClick={() => setShowAddModal(true)}>
            + Créer un agent
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="team-tabs">
        {isSousAdmin && (
          <button
            className={`team-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Mon Équipe ({agents.length + chefs.length})
          </button>
        )}
        <button
          className={`team-tab ${activeTab === 'equipe' ? 'active' : ''}`}
          onClick={() => setActiveTab('equipe')}
        >
          {isSousAdmin ? `Mes Agents Directs (${agents.length})` : `Mon Équipe (${agents.length})`}
        </button>
        {isSousAdmin && (
          <button
            className={`team-tab ${activeTab === 'chefs' ? 'active' : ''}`}
            onClick={() => setActiveTab('chefs')}
          >
            Mes Chefs d'Équipe ({chefs.length})
          </button>
        )}
        <button
          className={`team-tab ${activeTab === 'clients' ? 'active' : ''}`}
          onClick={() => setActiveTab('clients')}
        >
          Clients ({clients.length})
        </button>
        <button
          className={`team-tab ${activeTab === 'commissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('commissions')}
        >
          Commissions ({commissions.length})
        </button>
        <button
          className={`team-tab ${activeTab === 'bonus' ? 'active' : ''}`}
          onClick={() => setActiveTab('bonus')}
        >
          Bonus ({bonuses.length})
        </button>
      </div>

      {/* Stats commissions */}
      {commissionStats && (
        <div className="commission-stats">
          {!isSousAdmin && (
            <div className="stat-card base">
              <div className="stat-label">Ma part (commissions)</div>
              <div className="stat-value">{commissionStats.commissions_gagnees?.toFixed(2) || '0.00'} €</div>
            </div>
          )}
          {!isSousAdmin && (
            <div className="stat-card bonus">
              <div className="stat-label">Bonus donnés</div>
              <div className="stat-value">-{commissionStats.total_bonus_donnes?.toFixed(2) || '0.00'} €</div>
            </div>
          )}
          <div className="stat-card disponible">
            <div className="stat-label"><Wallet size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Solde disponible</div>
            <div className="stat-value">{commissionStats.disponible?.toFixed(2) || '0.00'} €</div>
          </div>
          {!isSousAdmin && (
            <div className="stat-card attribue">
              <div className="stat-label">Part agents (auto)</div>
              <div className="stat-value">{commissionStats.total_attribue?.toFixed(2) || '0.00'} €</div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Vue d'ensemble (sous_admin only) */}
      {activeTab === 'overview' && isSousAdmin && (
        <div className="tab-content">
          <div className="equipe-layout">
            <div className="agents-panel">
              {/* Section Chefs d'Équipe */}
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ background: '#f57c00', color: '#fff', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>{chefs.length}</span>
                Mes Chefs d'Équipe
              </h3>
              {chefs.length > 0 ? (
                <div className="agents-list-compact">
                  {chefs.map(chef => (
                    <div
                      key={`chef-${chef.id}`}
                      className={`agent-card ${selectedChef?.id === chef.id && !selectedAgent ? 'selected' : ''}`}
                      onClick={() => { handleSelectChef(chef); setSelectedAgent(null); setAgentStats(null); }}
                      style={{ borderLeft: '3px solid #f57c00' }}
                    >
                      <div className="agent-info">
                        <div className="agent-name">
                          {chef.nom}
                          <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', background: '#fff3e0', color: '#f57c00' }}>Chef</span>
                        </div>
                        <div className="agent-email">{chef.email}</div>
                      </div>
                      <div className="agent-commission" style={{ textAlign: 'right' }}>
                        <div>
                          <span className="commission-badge chef" style={{
                            background: parseFloat(chef.rapport_net || 0) > 0 ? '#e8f5e9' : '#fff3e0',
                            color: parseFloat(chef.rapport_net || 0) > 0 ? '#2e7d32' : '#f57c00',
                            fontWeight: '700'
                          }}>
                            {parseFloat(chef.rapport_net || 0).toFixed(2)} €
                          </span>
                        </div>
                        <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>
                          {parseInt(chef.clients_rapportes || 0)} client{parseInt(chef.clients_rapportes || 0) > 1 ? 's' : ''} · <Users size={10} style={{display:'inline',verticalAlign:'middle'}} /> {chef.nombre_agents || 0} agents
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-data" style={{ marginBottom: '15px' }}>
                  <p>Aucun chef d'équipe</p>
                </div>
              )}

              {/* Section Agents Directs */}
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '20px' }}>
                <span style={{ background: '#667eea', color: '#fff', borderRadius: '50%', width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>{agents.length}</span>
                Mes Agents Directs
              </h3>
              {agents.length > 0 ? (
                <div className="agents-list-compact">
                  {agents.map(agent => {
                    const rapportAgent = parseFloat(agent.rapport_net || 0);
                    return (
                      <div
                        key={`agent-${agent.id}`}
                        className={`agent-card ${selectedAgent?.id === agent.id ? 'selected' : ''}`}
                        onClick={() => { handleSelectAgent(agent); setSelectedChef(null); }}
                        style={{ borderLeft: '3px solid #667eea' }}
                      >
                        <div className="agent-info">
                          <div className="agent-name">{agent.nom}</div>
                          <div className="agent-email">{agent.email}</div>
                        </div>
                        <div className="agent-commission" style={{ textAlign: 'right' }}>
                          <div>
                            <span className={`commission-badge ${rapportAgent > 0 ? 'chef' : ''}`} style={{
                              background: rapportAgent > 0 ? '#e8f5e9' : '#f5f5f5',
                              color: rapportAgent > 0 ? '#2e7d32' : '#999'
                            }}>
                              {rapportAgent.toFixed(2)} €
                            </span>
                          </div>
                          <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>
                            {parseInt(agent.clients_rapportes || 0)} client{parseInt(agent.clients_rapportes || 0) > 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-data">
                  <p>Aucun agent direct</p>
                </div>
              )}
            </div>

            <div className="agent-details-panel">
              {/* Détails d'un chef sélectionné */}
              {selectedChef && !selectedAgent ? (
                <>
                  <div className="agent-header">
                    <h3>{selectedChef.nom}</h3>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: '#fff3e0',
                      color: '#f57c00'
                    }}>Chef d'Équipe</span>
                  </div>

                  <div className="agent-stats-grid">
                    <div className="agent-stat">
                      <span className="stat-label">Email</span>
                      <span className="stat-value" style={{ fontSize: '13px' }}>{selectedChef.email}</span>
                    </div>
                    <div className="agent-stat highlight">
                      <span className="stat-label">Commission / client</span>
                      <span className="stat-value">{parseFloat(selectedChef.commission_base || 0).toFixed(2)} €</span>
                    </div>
                    <div className="agent-stat">
                      <span className="stat-label">Nombre d'agents</span>
                      <span className="stat-value">{selectedChef.nombre_agents || 0}</span>
                    </div>
                    <div className="agent-stat">
                      <span className="stat-label">Créé le</span>
                      <span className="stat-value">{formatDate(selectedChef.created_at)}</span>
                    </div>
                  </div>

                  {chefAgents.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <h4 style={{ marginBottom: '10px', color: '#333' }}>Agents de {selectedChef.nom}</h4>
                      <div className="agents-list-compact">
                        {chefAgents.map(agent => (
                          <div
                            key={agent.id}
                            className={`agent-card ${selectedAgent?.id === agent.id ? 'selected' : ''}`}
                            onClick={() => handleSelectAgent(agent, selectedChef.id)}
                            style={{ borderLeft: '2px solid #667eea' }}
                          >
                            <div className="agent-info">
                              <div className="agent-name">{agent.nom}</div>
                              <div className="agent-email">{agent.email}</div>
                            </div>
                            <div className="agent-commission">
                              <span className="commission-badge chef">
                                {parseFloat(agent.commission_attribuee || 0).toFixed(2)} €
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {chefAgents.length === 0 && (
                    <div style={{ marginTop: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '8px', textAlign: 'center', color: '#888' }}>
                      Ce chef n'a pas encore d'agents
                    </div>
                  )}
                </>
              ) : selectedAgent && agentStats ? (
                <>
                  <div className="agent-header">
                    <h3>{selectedAgent.nom}</h3>
                    <div className="agent-actions-inline">
                      <button className="btn-remove" onClick={() => handleRemoveAgent(selectedAgent.id)}>
                        Retirer de l'équipe
                      </button>
                      {!isSousAdmin && (
                        <button className="btn-delete" onClick={() => handleDeleteAgent(selectedAgent.id)}>
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="agent-stats-grid">
                    {!isSousAdmin && (
                      <div className="agent-stat">
                        <span className="stat-label">Clients total</span>
                        <span className="stat-value">{agentStats.clients_total}</span>
                      </div>
                    )}
                    <div className="agent-stat">
                      <span className="stat-label">Clients payés</span>
                      <span className="stat-value">{agentStats.clients_payes}</span>
                    </div>
                    {!isSousAdmin && (
                      <div className="agent-stat">
                        <span className="stat-label">CA généré</span>
                        <span className="stat-value">{agentStats.ca_total.toFixed(2)} €</span>
                      </div>
                    )}
                    <div className="agent-stat highlight">
                      <span className="stat-label">Commission attribuée</span>
                      <span className="stat-value">{parseFloat(selectedAgent.commission_attribuee || 0).toFixed(2)} €</span>
                    </div>
                    <div className="agent-stat">
                      <span className="stat-label">Commissions gagnées</span>
                      <span className="stat-value">{agentStats.commissions_totales.toFixed(2)} €</span>
                    </div>
                    {isSousAdmin && selectedAgent.rapport_net !== undefined && (
                      <div className="agent-stat" style={{ background: '#e8f5e9' }}>
                        <span className="stat-label" style={{ color: '#2e7d32' }}>Commissions générées</span>
                        <span className="stat-value" style={{ color: '#2e7d32' }}>{parseFloat(selectedAgent.rapport_net || 0).toFixed(2)} €</span>
                      </div>
                    )}
                    {!isSousAdmin && (
                      <div className="agent-stat">
                        <span className="stat-label">Commissions payées</span>
                        <span className="stat-value">{agentStats.commissions_payees.toFixed(2)} €</span>
                      </div>
                    )}
                  </div>

                  <div className="commission-edit">
                    <label>Modifier commission attribuée :</label>
                    <div className="commission-input-row">
                      <input
                        type="number"
                        value={selectedAgent.commission_attribuee || 0}
                        onChange={(e) => {
                          setSelectedAgent({ ...selectedAgent, commission_attribuee: e.target.value });
                          setAgents(prev => prev.map(a =>
                            a.id === selectedAgent.id ? { ...a, commission_attribuee: e.target.value } : a
                          ));
                        }}
                        min="0"
                        step="0.01"
                      />
                      <span>€</span>
                      <button
                        className="btn-save-commission"
                        onClick={() => handleUpdateCommission(selectedAgent.id, selectedAgent.commission_attribuee)}
                      >
                        Enregistrer
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: '10px', color: '#666', fontSize: '12px' }}>
                      Cette commission sera visible par l'agent dans son compte.
                    </small>
                  </div>
                </>
              ) : (
                <div className="no-selection">
                  <p>Sélectionnez un membre pour voir ses détails</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Équipe (agents directs) */}
      {activeTab === 'equipe' && (
        <div className="tab-content">
          <div className="equipe-layout">
            <div className="agents-panel">
              <h3>Mes Agents</h3>
              {agents.length === 0 ? (
                <div className="no-data">
                  <p>Aucun agent dans votre équipe.</p>
                </div>
              ) : (
                <div className="agents-list-compact">
                  {agents.map(agent => {
                    const commission = getCommissionDisplay(agent);
                    return (
                      <div
                        key={agent.id}
                        className={`agent-card ${selectedAgent?.id === agent.id ? 'selected' : ''}`}
                        onClick={() => handleSelectAgent(agent)}
                      >
                        <div className="agent-info">
                          <div className="agent-name">{agent.nom}</div>
                          <div className="agent-email">{agent.email}</div>
                        </div>
                        <div className="agent-commission">
                          <span className={`commission-badge ${commission.source}`}>
                            {commission.value.toFixed(2)} €
                          </span>
                          <button
                            className="btn-bonus-small"
                            onClick={(e) => { e.stopPropagation(); openBonusModal(agent); }}
                            title="Donner un bonus"
                          >
                            <Gift size={14} style={{display:'inline',verticalAlign:'middle'}} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="agent-details-panel">
              {selectedAgent && agentStats ? (
                <>
                  <div className="agent-header">
                    <h3>{selectedAgent.nom}</h3>
                    <div className="agent-actions-inline">
                      <button className="btn-remove" onClick={() => handleRemoveAgent(selectedAgent.id)}>
                        Retirer de l'équipe
                      </button>
                      {!isSousAdmin && (
                        <button className="btn-delete" onClick={() => handleDeleteAgent(selectedAgent.id)}>
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="agent-stats-grid">
                    {!isSousAdmin && (
                      <div className="agent-stat">
                        <span className="stat-label">Clients total</span>
                        <span className="stat-value">{agentStats.clients_total}</span>
                      </div>
                    )}
                    <div className="agent-stat">
                      <span className="stat-label">Clients payés</span>
                      <span className="stat-value">{agentStats.clients_payes}</span>
                    </div>
                    {!isSousAdmin && (
                      <div className="agent-stat">
                        <span className="stat-label">CA généré</span>
                        <span className="stat-value">{agentStats.ca_total.toFixed(2)} €</span>
                      </div>
                    )}
                    <div className="agent-stat highlight">
                      <span className="stat-label">Commission attribuée</span>
                      <span className="stat-value">{parseFloat(selectedAgent.commission_attribuee || 0).toFixed(2)} €</span>
                    </div>
                    <div className="agent-stat">
                      <span className="stat-label">Commissions gagnées</span>
                      <span className="stat-value">{agentStats.commissions_totales.toFixed(2)} €</span>
                    </div>
                    {isSousAdmin && selectedAgent.rapport_net !== undefined && (
                      <div className="agent-stat" style={{ background: '#e8f5e9' }}>
                        <span className="stat-label" style={{ color: '#2e7d32' }}>Commissions générées</span>
                        <span className="stat-value" style={{ color: '#2e7d32' }}>{parseFloat(selectedAgent.rapport_net || 0).toFixed(2)} €</span>
                      </div>
                    )}
                    {!isSousAdmin && (
                      <div className="agent-stat">
                        <span className="stat-label">Commissions payées</span>
                        <span className="stat-value">{agentStats.commissions_payees.toFixed(2)} €</span>
                      </div>
                    )}
                  </div>

                  <div className="commission-edit">
                    <label>Modifier commission attribuée (Chef) :</label>
                    <div className="commission-input-row">
                      <input
                        type="number"
                        value={selectedAgent.commission_attribuee || 0}
                        onChange={(e) => {
                          setSelectedAgent({ ...selectedAgent, commission_attribuee: e.target.value });
                          setAgents(prev => prev.map(a =>
                            a.id === selectedAgent.id ? { ...a, commission_attribuee: e.target.value } : a
                          ));
                        }}
                        min="0"
                        step="0.01"
                      />
                      <span>€</span>
                      <button
                        className="btn-save-commission"
                        onClick={() => handleUpdateCommission(selectedAgent.id, selectedAgent.commission_attribuee)}
                      >
                        Enregistrer
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: '10px', color: '#666', fontSize: '12px' }}>
                      Cette commission sera visible par l'agent dans son compte.
                    </small>
                  </div>
                </>
              ) : (
                <div className="no-selection">
                  <p>Sélectionnez un agent pour voir ses détails</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Mes Chefs d'Équipe (sous_admin only) */}
      {activeTab === 'chefs' && isSousAdmin && (
        <div className="tab-content">
          <div className="equipe-layout">
            <div className="agents-panel">
              <h3>Mes Chefs d'Équipe</h3>
              {chefs.length === 0 ? (
                <div className="no-data">
                  <p>Aucun chef d'équipe créé.</p>
                  <button className="btn-add" style={{ marginTop: '10px' }} onClick={() => setShowCreateChefModal(true)}>
                    + Créer un chef d'équipe
                  </button>
                </div>
              ) : (
                <div className="agents-list-compact">
                  {chefs.map(chef => (
                    <div
                      key={chef.id}
                      className={`agent-card ${selectedChef?.id === chef.id ? 'selected' : ''}`}
                      onClick={() => handleSelectChef(chef)}
                      style={{ borderLeft: '3px solid #f57c00' }}
                    >
                      <div className="agent-info">
                        <div className="agent-name">{chef.nom}</div>
                        <div className="agent-email">{chef.email}</div>
                      </div>
                      <div className="agent-commission">
                        <span className="commission-badge chef" style={{ background: '#fff3e0', color: '#f57c00' }}>
                          {parseFloat(chef.commission_base || 0).toFixed(2)} € / client
                        </span>
                        <span style={{ fontSize: '11px', color: '#888', marginLeft: '5px' }}>
                          <Users size={12} style={{display:'inline',verticalAlign:'middle'}} /> {chef.nombre_agents || 0}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="agent-details-panel">
              {selectedChef ? (
                <>
                  <div className="agent-header">
                    <h3>{selectedChef.nom}</h3>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: '#fff3e0',
                      color: '#f57c00'
                    }}>Chef d'Équipe</span>
                  </div>

                  <div className="agent-stats-grid">
                    <div className="agent-stat">
                      <span className="stat-label">Email</span>
                      <span className="stat-value" style={{ fontSize: '13px' }}>{selectedChef.email}</span>
                    </div>
                    <div className="agent-stat highlight">
                      <span className="stat-label">Commission / client</span>
                      <span className="stat-value">{parseFloat(selectedChef.commission_base || 0).toFixed(2)} €</span>
                    </div>
                    <div className="agent-stat">
                      <span className="stat-label">Nombre d'agents</span>
                      <span className="stat-value">{selectedChef.nombre_agents || 0}</span>
                    </div>
                    <div className="agent-stat">
                      <span className="stat-label">Créé le</span>
                      <span className="stat-value">{formatDate(selectedChef.created_at)}</span>
                    </div>
                  </div>

                  {/* Agents de ce chef */}
                  {chefAgents.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <h4 style={{ marginBottom: '10px', color: '#333' }}>Agents de {selectedChef.nom}</h4>
                      <div className="agents-list-compact">
                        {chefAgents.map(agent => (
                          <div
                            key={agent.id}
                            className={`agent-card ${selectedAgent?.id === agent.id ? 'selected' : ''}`}
                            onClick={() => handleSelectAgent(agent, selectedChef.id)}
                            style={{ borderLeft: '2px solid #667eea' }}
                          >
                            <div className="agent-info">
                              <div className="agent-name">{agent.nom}</div>
                              <div className="agent-email">{agent.email}</div>
                            </div>
                            <div className="agent-commission">
                              <span className="commission-badge chef">
                                {parseFloat(agent.commission_attribuee || 0).toFixed(2)} €
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {chefAgents.length === 0 && (
                    <div style={{ marginTop: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '8px', textAlign: 'center', color: '#888' }}>
                      Ce chef n'a pas encore d'agents
                    </div>
                  )}
                </>
              ) : (
                <div className="no-selection">
                  <p>Sélectionnez un chef d'équipe pour voir ses détails</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Clients */}
      {activeTab === 'clients' && (
        <div className="tab-content">
          <h3>Clients de mon équipe</h3>
          {clients.length === 0 ? (
            <div className="no-data">
              <p>Aucun client pour le moment.</p>
            </div>
          ) : (
            <div className="clients-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Client</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Montant</th>
                    <th>Statut</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map(client => (
                    <tr key={client.id}>
                      <td>
                        <span className={`agent-badge ${client.is_direct_client ? 'chef' : ''}`}>
                          {client.is_direct_client ? 'Moi (direct)' : client.agent_nom}
                        </span>
                      </td>
                      <td className="client-name">{client.nom}</td>
                      <td>{client.email}</td>
                      <td>{client.telephone}</td>
                      <td className="amount">
                        {client.montant_bon_commande ? `${parseFloat(client.montant_bon_commande).toFixed(2)} €` : '-'}
                      </td>
                      <td>
                        <span className={`status-badge ${client.paye ? 'paid' : client.client_signe ? 'signed' : 'pending'}`}>
                          {client.paye ? 'Payé' : client.client_signe ? 'Signé' : 'En attente'}
                        </span>
                      </td>
                      <td>{formatDate(client.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Commissions */}
      {activeTab === 'commissions' && (
        <div className="tab-content">
          <h3>Historique des commissions</h3>
          {commissions.length === 0 ? (
            <div className="no-data">
              <p>Aucune commission pour le moment.</p>
            </div>
          ) : (
            <div className="commissions-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Bénéficiaire</th>
                    <th>Client</th>
                    <th>Montant payé</th>
                    <th>Commission</th>
                    <th>Type</th>
                    <th>Statut</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map(comm => {
                    const isChefCommission = comm.type_commission === 'chef';
                    const typeLabel = isChefCommission ? 'Ma commission' : 'Redistribué';
                    const beneficiaire = isChefCommission ? 'Moi' : comm.agent_nom;
                    return (
                      <tr key={comm.id}>
                        <td>
                          <span className={`agent-badge ${isChefCommission ? 'chef' : ''}`}>{beneficiaire}</span>
                        </td>
                        <td>{comm.client_nom}</td>
                        <td className="amount">{parseFloat(comm.montant_paye || 0).toFixed(2)} €</td>
                        <td className="amount highlight">{parseFloat(comm.commission_agent || 0).toFixed(2)} €</td>
                        <td>
                          <span className={`type-badge ${isChefCommission ? 'admin' : 'chef'}`}>{typeLabel}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${comm.commission_agent_payee ? 'paid' : 'pending'}`}>
                            {comm.commission_agent_payee ? 'Payée' : 'En attente'}
                          </span>
                        </td>
                        <td>{formatDate(comm.date_paiement || comm.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Créer un agent */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Créer un nouvel agent</h3>
              <button className="btn-close" onClick={() => setShowAddModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateAgent}>
              <div className="form-group">
                <label>Nom complet *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={e => setFormData({ ...formData, nom: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Mot de passe *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Commission attribuée (€)</label>
                <input
                  type="number"
                  value={formData.commission_attribuee}
                  onChange={e => setFormData({ ...formData, commission_attribuee: e.target.value })}
                  min="0"
                  step="0.01"
                />
                <small>{isSousAdmin
                  ? `Vous avez une commission de ${commissionStats?.commission_par_client?.toFixed(2) || '0.00'} € par client. Vous êtes libre d'attribuer de 0 à ${commissionStats?.commission_par_client?.toFixed(2) || '0.00'} € (la totalité de votre commission).`
                  : `Disponible: ${commissionStats?.disponible?.toFixed(2) || '0.00'} €`
                }</small>
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowAddModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-save">
                  Créer l'agent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Créer un chef d'équipe (sous_admin) */}
      {showCreateChefModal && isSousAdmin && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Créer un chef d'équipe</h3>
              <button className="btn-close" onClick={() => setShowCreateChefModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateChef}>
              <div className="form-group">
                <label>Nom complet *</label>
                <input
                  type="text"
                  value={chefFormData.nom}
                  onChange={e => setChefFormData({ ...chefFormData, nom: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={chefFormData.email}
                  onChange={e => setChefFormData({ ...chefFormData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Mot de passe *</label>
                <input
                  type="password"
                  value={chefFormData.password}
                  onChange={e => setChefFormData({ ...chefFormData, password: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Commission de base par client (€)</label>
                <input
                  type="number"
                  value={chefFormData.commission_base}
                  onChange={e => setChefFormData({ ...chefFormData, commission_base: e.target.value })}
                  min="0"
                  step="0.01"
                />
                <small style={{color: '#666', fontSize: '12px'}}>
                  Montant que ce chef recevra par client payé, qu'il pourra redistribuer à ses agents
                </small>
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowCreateChefModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-save" style={{ background: '#f57c00' }}>
                  Créer le chef d'équipe
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tab: Bonus */}
      {activeTab === 'bonus' && (
        <div className="tab-content">
          <h3>Historique des bonus accordés</h3>
          {bonuses.length === 0 ? (
            <div className="no-data">
              <p>Aucun bonus accordé pour le moment.</p>
            </div>
          ) : (
            <div className="bonuses-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Montant</th>
                    <th>Motif</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bonuses.map(bonus => (
                    <tr key={bonus.id}>
                      <td>{bonus.agent_nom}</td>
                      <td className="amount highlight">{parseFloat(bonus.montant).toFixed(2)} €</td>
                      <td>{bonus.motif || '-'}</td>
                      <td>{formatDate(bonus.created_at)}</td>
                      <td>
                        <button
                          className="btn-delete-bonus"
                          onClick={() => handleDeleteBonus(bonus.id, parseFloat(bonus.montant).toFixed(2), bonus.agent_nom)}
                          title="Supprimer ce bonus"
                        >
                          <Trash2 size={14} style={{display:'inline',verticalAlign:'middle'}} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Bonus */}
      {showBonusModal && bonusAgent && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3><Gift size={18} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Donner un bonus à {bonusAgent.nom}</h3>
              <button className="btn-close" onClick={() => setShowBonusModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleGiveBonus}>
              <div className="bonus-info" style={{
                background: '#e8f5e9',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <strong>Solde disponible :</strong> {commissionStats?.disponible?.toFixed(2) || '0.00'} €
              </div>
              <div className="form-group">
                <label>Montant du bonus (€) *</label>
                <input
                  type="number"
                  value={bonusData.montant}
                  onChange={e => setBonusData({ ...bonusData, montant: e.target.value })}
                  min="0.01"
                  max={commissionStats?.disponible || 0}
                  step="0.01"
                  required
                  placeholder={`Max: ${commissionStats?.disponible?.toFixed(2) || 0} €`}
                />
              </div>
              <div className="form-group">
                <label>Motif (optionnel)</label>
                <input
                  type="text"
                  value={bonusData.motif}
                  onChange={e => setBonusData({ ...bonusData, motif: e.target.value })}
                  placeholder="Ex: Performance exceptionnelle"
                />
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowBonusModal(false)}>
                  Annuler
                </button>
                <button type="submit" className="btn-save" disabled={!bonusData.montant || parseFloat(bonusData.montant) > (commissionStats?.disponible || 0)}>
                  Accorder le bonus
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamManagement;
